/**
 * Extracts the best available human-readable error message from a caught error.
 *
 * - If the error is an `APIError` (identified by `name`) with a real backend
 *   message (not a bare HTTP status fallback like "HTTP 500"), that message is
 *   returned so the user sees the specific reason from the server.
 * - Otherwise the provided `fallback` string is returned.
 *
 * Uses duck-typing on `name` instead of `instanceof` so this utility works
 * correctly in test environments where `@/lib/api/client` is mocked.
 *
 * @example
 * catch (err) {
 *   const message = extractApiError(err, t("common.error.generic"));
 *   toast.error({ message });
 * }
 */
export function extractApiError(err: unknown, fallback: string): string {
  // Duck-typed check so mocked APIError instances (or cross-realm errors)
  // that expose `name` and `message` still work correctly.
  if (err && typeof err === "object") {
    const maybeError = err as { name?: unknown; message?: unknown };
    const name = maybeError.name;
    const message = maybeError.message;

    if (
      name === "APIError" &&
      typeof message === "string" &&
      message.length > 0 &&
      !message.startsWith("HTTP ")
    ) {
      return message;
    }
  }
  return fallback;
}
